/* eslint-disable no-unused-vars */
import React, { useState, useMemo } from 'react';
import { PLATS, NM, COST_FIELDS, PRE_MONTHS } from '../utils/constants';
import { calcPlatform, calcRoyalties } from '../utils/calc';
import { ml, fu } from '../utils/format';
import { getCostState, getDealTerms } from '../utils/stateFactory';

const OH_KEY     = 'pfm_overheads';
const PEOPLE_KEY = 'pfm_people';

function loadOH(key) {
  try { const d = localStorage.getItem(key); return d ? JSON.parse(d) : null; } catch (_) { return null; }
}
function saveOH(key, data) {
  try { localStorage.setItem(key, JSON.stringify(data)); } catch (_) {}
}
function makeRows(labels) {
  return labels.map(label => ({ label, values: Array(NM).fill('') }));
}

const PEOPLE_LABELS = ['Salaries', 'Contractor Costs', 'Other People Costs'];
const OH_LABELS     = ['Marketing', 'Premises', 'Technology', 'Legal & Professional', 'Other Overheads'];

function sumRows(rows) {
  return Array.from({ length: NM }, (_, i) =>
    rows.reduce((a, r) => a + (parseFloat(r.values[i]) || 0), 0)
  );
}

// Annual total: sum months startIdx to startIdx+11
function annualSum(arr, startIdx) {
  let t = 0;
  for (let i = startIdx; i < Math.min(startIdx + 12, NM); i++) t += (arr[i] || 0);
  return t;
}

// Year label from startDate
function yearLabel(yearNum, startDate) {
  if (!startDate) return `Y${yearNum + 1} Total`;
  const year = startDate.year + yearNum;
  return `${year} Total`;
}

// Full month+annual header
function FullHeader({ label, startDate }) {
  const annualCols = Array.from({ length: 3 }, (_, y) => (
    <th key={`y${y}`} style={{ background: 'var(--s2)', color: 'var(--blue)', fontWeight: 700, borderLeft: '2px solid var(--e2)' }}>
      {yearLabel(y, startDate)}
    </th>
  ));
  const monthCols = Array.from({ length: NM }, (_, i) => <th key={i}>{ml(i, startDate)}</th>);
  return <tr><th>{label}</th>{annualCols}{monthCols}</tr>;
}

// Row with annual totals + monthly values
function CalcRow({ label, values, cls = 'rc', convert, fc, startDate }) {
  const converted = values.map((v, i) => convert ? convert(v || 0, fc, i) : (v || 0));
  const annuals = [0, 1, 2].map(y => annualSum(converted, y * 12));
  return (
    <tr className={cls}>
      <td>{label}</td>
      {annuals.map((v, y) => (
        <td key={`y${y}`} style={{ background: 'var(--s2)', fontWeight: 600, borderLeft: '2px solid var(--e2)', color: v !== 0 ? undefined : 'var(--t4)' }}>
          {v !== 0 ? fu(v) : '—'}
        </td>
      ))}
      {converted.map((v, i) => <td key={i}>{v !== 0 ? fu(v) : '—'}</td>)}
    </tr>
  );
}

function EditableRow({ row, rowIdx, onChange, convert, fc }) {
  const converted = row.values.map((v, i) => convert ? convert(parseFloat(v) || 0, fc, i) : (parseFloat(v) || 0));
  const annuals = [0, 1, 2].map(y => annualSum(converted, y * 12));
  return (
    <tr className="ri">
      <td>{row.label}</td>
      {annuals.map((v, y) => (
        <td key={`y${y}`} style={{ background: 'var(--s2)', fontWeight: 600, borderLeft: '2px solid var(--e2)', color: 'var(--t2)' }}>
          {v !== 0 ? fu(v) : '—'}
        </td>
      ))}
      {row.values.map((v, i) => (
        <td key={i}>
          <input type="number" className="ci" value={v}
            onChange={e => onChange(rowIdx, i, e.target.value)} />
        </td>
      ))}
    </tr>
  );
}

function SpacerRow() { return <tr className="rx"><td colSpan={NM + 4} /></tr>; }
function SectionHeader({ label }) {
  return <tr className="rh"><td colSpan={NM + 4}>{label}</td></tr>;
}

function PnLContent({ games, PS, CS, DT, startDate = null, functionalCurrency, convert, peopleDevTotal, peopleOhTotal, ohCategories = [], ohValues = {}, getCategoryMonthlyTotal, getOhAllMonthlyTotal, plActuals, plMappings, currentMonthIdx }) {
  const fc = functionalCurrency || 'USD';

  const [peopleRows, setPeopleRows] = useState(() => loadOH(PEOPLE_KEY) || makeRows(PEOPLE_LABELS));
  const [ohRows, setOhRows]         = useState(() => loadOH(OH_KEY) || makeRows(OH_LABELS));

  const updatePeople = (rowIdx, colIdx, val) => {
    const next = peopleRows.map((r, i) =>
      i === rowIdx ? { ...r, values: r.values.map((v, j) => j === colIdx ? val : v) } : r
    );
    setPeopleRows(next);
    saveOH(PEOPLE_KEY, next);
  };

  const updateOH = (rowIdx, colIdx, val) => {
    const next = ohRows.map((r, i) =>
      i === rowIdx ? { ...r, values: r.values.map((v, j) => j === colIdx ? val : v) } : r
    );
    setOhRows(next);
    saveOH(OH_KEY, next);
  };

  const calc = useMemo(() => {
    const grossByGame = {};
    const totalGross  = Array(NM).fill(0);
    const totalFees   = Array(NM).fill(0);
    const totalNet    = Array(NM).fill(0);

    games.forEach(g => {
      const gt = Array(NM).fill(0);
      PLATS.forEach(p => {
        const st = PS[g]?.[p.key];
        if (!st?.enabled) return;
        const rc = calcPlatform(p.key, st);
        rc.grossRaw.forEach((v, i) => {
          if (v !== null) {
            const fee = v - (rc.netRaw[i] ?? 0);
            gt[i]         += v;
            totalGross[i] += v;
            totalFees[i]  += fee;
            totalNet[i]   += rc.netRaw[i] ?? 0;
          }
        });
      });
      grossByGame[g] = gt;
    });

    return { grossByGame, totalGross, totalFees, totalNet };
  }, [games, PS]);

  const cos = useMemo(() => {
    const recDevAdv = Array(NM).fill(0), recPubCosts = Array(NM).fill(0);
    const recMkt = Array(NM).fill(0), nonRecoup = Array(NM).fill(0), royalties = Array(NM).fill(0);

    games.forEach(g => {
      const costs = getCostState(CS, g);
      const dt    = getDealTerms(DT, g);

      // P&L only uses forecast months (array indices PRE_MONTHS to PRE_MONTHS+NM-1)
      costs.recoup.devAdvances.forEach((v, arrayIdx) => { const i = arrayIdx - PRE_MONTHS; if (i >= 0 && i < NM) recDevAdv[i]   += parseFloat(v) || 0; });
      costs.recoup.loc.forEach((v, arrayIdx)          => { const i = arrayIdx - PRE_MONTHS; if (i >= 0 && i < NM) recPubCosts[i] += parseFloat(v) || 0; });
      costs.recoup.qa.forEach((v, arrayIdx)            => { const i = arrayIdx - PRE_MONTHS; if (i >= 0 && i < NM) recPubCosts[i] += parseFloat(v) || 0; });
      costs.recoup.otherPub.forEach((v, arrayIdx)      => { const i = arrayIdx - PRE_MONTHS; if (i >= 0 && i < NM) recPubCosts[i] += parseFloat(v) || 0; });
      costs.recoup.marketing.forEach((v, arrayIdx)     => { const i = arrayIdx - PRE_MONTHS; if (i >= 0 && i < NM) recMkt[i]      += parseFloat(v) || 0; });
      COST_FIELDS.forEach(({ key }) => {
        costs.nonRecoup[key].forEach((v, arrayIdx) => { const i = arrayIdx - PRE_MONTHS; if (i >= 0 && i < NM) nonRecoup[i] += parseFloat(v) || 0; });
      });

      const netArr = Array(NM).fill(0), recArr = Array(NM).fill(0);
      PLATS.forEach(p => {
        const st = PS[g]?.[p.key];
        if (!st?.enabled) return;
        calcPlatform(p.key, st).netRaw.forEach((v, i) => { if (v !== null) netArr[i] += v; });
      });
      costs.recoup.devAdvances.forEach((v, ai) => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) recArr[i] += parseFloat(v) || 0; });
      costs.recoup.loc.forEach((v, ai)          => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) recArr[i] += parseFloat(v) || 0; });
      costs.recoup.qa.forEach((v, ai)            => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) recArr[i] += parseFloat(v) || 0; });
      costs.recoup.otherPub.forEach((v, ai)      => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) recArr[i] += parseFloat(v) || 0; });
      costs.recoup.marketing.forEach((v, ai)     => { const i = ai - PRE_MONTHS; if (i >= 0 && i < NM) recArr[i] += parseFloat(v) || 0; });

      const { royaltyPayableRaw } = calcRoyalties(netArr, recArr, dt);
      royaltyPayableRaw.forEach((v, i) => { royalties[i] += v || 0; });
    });

    const totalCOS = Array(NM).fill(0);
    for (let i = 0; i < NM; i++) totalCOS[i] = recDevAdv[i] + recPubCosts[i] + recMkt[i] + nonRecoup[i] + royalties[i];
    return { recDevAdv, recPubCosts, recMkt, nonRecoup, royalties, totalCOS };
  }, [games, PS, CS, DT]);

  // Convert totalNet (USD) to FC before subtracting FC-denominated costs
  const totalNetFC = useMemo(() =>
    calc.totalNet.map((v, i) => convert ? convert(v || 0, fc, i) : (v || 0)),
    [calc.totalNet, convert, fc]
  );

  const grossProfit = useMemo(() =>
    Array.from({ length: NM }, (_, i) => totalNetFC[i] - cos.totalCOS[i]),
    [totalNetFC, cos.totalCOS]
  );

  const peopleTotals = useMemo(() => sumRows(peopleRows), [peopleRows]);
  const ohTotals     = useMemo(() => sumRows(ohRows), [ohRows]);
  const totalOH      = useMemo(() =>
    Array.from({ length: NM }, (_, i) => peopleTotals[i] + ohTotals[i]), [peopleTotals, ohTotals]);
  // ohCatTotal = sum of all non-belowEbitda overhead categories
  const ohCatTotal = useMemo(() => {
    if (!ohCategories?.length) return Array(NM).fill(0);
    return Array.from({ length: NM }, (_, i) =>
      (ohCategories || []).filter(c => !c.belowEbitda).reduce((s, cat) => {
        const totals = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : [];
        return s + (totals[i] || 0);
      }, 0)
    );
  }, [ohCategories, getCategoryMonthlyTotal]);

  // totalOH for display and EBITDA calc
  const totalOHCalc = useMemo(() =>
    Array.from({ length: NM }, (_, i) =>
      (ohCategories?.length > 0
        ? (peopleOhTotal?.[i] || peopleTotals[i]) + (ohCatTotal[i] || 0)
        : peopleTotals[i] + ohTotals[i])
    ), [ohCategories, peopleOhTotal, peopleTotals, ohCatTotal, ohTotals]);

  const ebitda = useMemo(() =>
    Array.from({ length: NM }, (_, i) => grossProfit[i] - totalOHCalc[i]),
    [grossProfit, totalOHCalc]);

  const activeGames = games.filter(g => PLATS.some(p => PS[g]?.[p.key]?.enabled));
  const colSpanTotal = NM + 4; // 3 annual + 36 monthly + 1 label

  const rowProps = { convert, fc };       // for revenue rows (USD → FC)
  const costProps = { convert: null, fc }; // costs already in FC, no conversion

  return (
    <div className="panel">
      <div className="card">
        <div className="card-head">
          <span className="card-title">Profit & Loss</span>
          <span className="card-meta">36-Month Forecast · All Games{fc !== 'USD' ? ` · ${fc}` : ''}</span>
        </div>
        <div className="card-body">
          <div className="tw">
            <table className="dt">
              <thead><FullHeader label="Line Item" startDate={startDate} /></thead>
              <tbody>
                <SectionHeader label="Gross Revenue" />
                {activeGames.map(g => {
                  const converted = calc.grossByGame[g]?.map((v, i) => convert ? convert(v || 0, fc, i) : (v || 0)) || [];
                  const annuals = [0,1,2].map(y => annualSum(converted, y * 12));
                  return (
                    <tr key={g} className="rc">
                      <td style={{ paddingLeft: 16 }}>{g}</td>
                      {annuals.map((v, y) => (
                        <td key={`y${y}`} style={{ background:'var(--s2)', fontWeight:600, borderLeft:'2px solid var(--e2)' }}>
                          {v > 0 ? fu(v) : '—'}
                        </td>
                      ))}
                      {converted.map((v, i) => <td key={i}>{v > 0 ? fu(v) : '—'}</td>)}
                    </tr>
                  );
                })}
                <CalcRow label="Total Gross Revenue" values={calc.totalGross} cls="rt" {...rowProps} />
                <CalcRow label="Platform Fees"       values={calc.totalFees.map(v => -v)} cls="rc" {...rowProps} />
                <CalcRow label="Total Net Revenue"   values={calc.totalNet} cls="rg" {...rowProps} />
                <SpacerRow />
                <SectionHeader label="Cost of Sales" />
                <CalcRow label="Development Advances (Recoupable)"  values={cos.recDevAdv}   cls="rc" {...costProps} />
                <CalcRow label="Publishing Costs (Recoupable)"       values={cos.recPubCosts} cls="rc" {...costProps} />
                <CalcRow label="Marketing Costs (Recoupable)"        values={cos.recMkt}      cls="rc" {...costProps} />
                <CalcRow label="Non-Recoupable Costs"                values={cos.nonRecoup}   cls="rc" {...costProps} />
                <CalcRow label="Royalties Payable"                   values={cos.royalties}   cls="rc" {...costProps} />
                {peopleDevTotal?.some(v => v > 0) && (() => {
                  // For locked months, use Total Salary Costs (mapped actuals) if available
                  const salaryVals = Array.from({ length: NM }, (_, i) => {
                    const isLocked = currentMonthIdx !== null && i <= currentMonthIdx;
                    if (isLocked && startDate && plActuals && plMappings) {
                      const totalMonths = startDate.month + i;
                      const yr = startDate.year + Math.floor(totalMonths / 12);
                      const mo = ((totalMonths % 12) + 12) % 12;
                      const mk = `${yr}-${String(mo+1).padStart(2,'0')}`;
                      let t = 0;
                      Object.entries(plActuals[mk] || {}).forEach(([acc, val]) => {
                        if (['salaries','contractors','other_staff'].includes(plMappings[acc]?.type)) t += parseFloat(val) || 0;
                      });
                      return t > 0 ? t : (peopleDevTotal?.[i] || 0);
                    }
                    return peopleDevTotal?.[i] || 0;
                  });
                  return <CalcRow label="Developer Payroll" values={salaryVals} cls="rc" {...costProps} />;
                })()}
                <CalcRow label="Total Cost of Sales"
                  values={Array.from({length:NM},(_,i)=>cos.totalCOS[i]+(peopleDevTotal?.[i]||0))}
                  cls="rt" {...costProps} />
                <SpacerRow />
                <CalcRow label="Gross Profit" values={grossProfit} cls="rg" {...costProps} />
                <SpacerRow />
                <SectionHeader label="Overheads" />
                <CalcRow label="People Costs" values={peopleOhTotal || peopleTotals} cls="rc" {...costProps} />
                {['contractors','other_staff'].map(type => {
                  const typeLabel = type === 'contractors' ? 'Contractors' : 'Other Staff Costs';
                  const vals = Array.from({ length: NM }, (_, i) => {
                    const isLocked = currentMonthIdx !== null && i <= currentMonthIdx;
                    if (isLocked && startDate && plActuals && plMappings) {
                      const totalMonths = startDate.month + i;
                      const yr = startDate.year + Math.floor(totalMonths / 12);
                      const mo = ((totalMonths % 12) + 12) % 12;
                      const mk = `${yr}-${String(mo+1).padStart(2,'0')}`;
                      let t = 0;
                      Object.entries(plActuals[mk] || {}).forEach(([acc, val]) => {
                        if (plMappings[acc]?.type === type) t += parseFloat(val) || 0;
                      });
                      return t;
                    }
                    return 0;
                  });
                  if (!vals.some(v => v > 0)) return null;
                  return <CalcRow key={type} label={typeLabel} values={vals} cls="rc" {...costProps} />;
                })}
                {(ohCategories || []).filter(c => !c.belowEbitda).map(cat => {
                  const catTotals = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : Array(NM).fill(0);
                  if (!catTotals.some(v => v > 0)) return null;
                  return <CalcRow key={cat.id} label={cat.name} values={catTotals} cls="rc" {...costProps} />;
                })}
                {(ohCategories || []).length === 0 && ohRows.map((row, ri) => (
                  <EditableRow key={row.label} row={row} rowIdx={ri} onChange={updateOH} convert={null} fc={fc} />
                ))}
                <CalcRow label="Total Overheads" values={totalOHCalc} cls="rt" {...costProps} />
                <SpacerRow />
                <CalcRow label="EBITDA" values={ebitda} cls="rg" {...costProps} />
                <SpacerRow />
                {/* Below EBITDA items */}
                {(ohCategories || []).filter(c => c.belowEbitda).map(cat =>
                  (cat.subs || []).map(sub => {
                    // Use getCategoryMonthlyTotal which already handles actuals via the wrapper
                    // For individual subs, compute inline using same logic
                    const subVals = Array.from({ length: NM }, (_, i) => {
                      const isLocked = currentMonthIdx !== null && i <= currentMonthIdx;
                      if (isLocked && startDate && plActuals && plMappings) {
                        const totalMonths = startDate.month + i;
                        const yr = startDate.year + Math.floor(totalMonths / 12);
                        const mo = ((totalMonths % 12) + 12) % 12;
                        const mk = `${yr}-${String(mo+1).padStart(2,'0')}`;
                        let t = 0;
                        Object.entries(plActuals[mk] || {}).forEach(([acc, val]) => {
                          const m = plMappings[acc];
                          if (m?.targetId === sub.id) t += parseFloat(val) || 0;
                        });
                        return t;
                      }
                      return parseFloat(ohValues?.[sub.id]?.[i]) || 0;
                    });
                    if (!subVals.some(v => v !== 0)) return null;
                    return <CalcRow key={sub.id} label={sub.name} values={subVals} cls="rc" {...costProps} />;
                  })
                )}
                {(() => {
                  const belowCats = (ohCategories || []).filter(c => c.belowEbitda);
                  if (!belowCats.length) return null;
                  const belowTotals = Array.from({ length: NM }, (_, i) => {
                    let t = 0;
                    belowCats.forEach(cat => {
                      (cat.subs || []).forEach(sub => {
                        t += parseFloat(ohValues?.[sub.id]?.[i]) || 0;
                      });
                    });
                    return t;
                  });
                  const pbt = ebitda.map((v, i) => v - belowTotals[i]);
                  return (
                    <>
                      <SpacerRow />
                      <CalcRow label="Profit Before Tax" values={pbt} cls="rg" {...costProps} />
                      <SpacerRow />
                      {(() => {
                        // Corporation Tax — stored as overhead sub 'corp_tax' in below_ebitda cat
                        const corpTaxVals = Array.from({ length: NM }, (_, i) =>
                          parseFloat(ohValues?.['corp_tax']?.[i]) || 0
                        );
                        const pat = pbt.map((v, i) => v - corpTaxVals[i]);
                        return (
                          <>
                            <CalcRow label="Corporation Tax" values={corpTaxVals.map(v => -v)} cls="rc" {...costProps} />
                            <CalcRow label="Profit After Tax" values={pat} cls="rg" {...costProps} />
                          </>
                        );
                      })()}
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
          {fc !== 'USD' && (
            <p style={{ fontSize:10, color:'var(--t4)', marginTop:10, padding:'6px 8px',
              border:'1px solid var(--e0)', borderRadius:'var(--r)', background:'var(--s1)',
              borderLeft:'3px solid var(--amber)' }}>
              ⚠ Amounts shown in {fc} using monthly budget FX rates. Set rates in Standing Data.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FinStatements({ games, PS, CS, DT, startDate = null, functionalCurrency, convert, cashflowData, peopleDevTotal, peopleOhTotal, canSeePL = true, canSeeCF = true, ohCategories = [], ohValues = {}, getCategoryMonthlyTotal, getOhAllMonthlyTotal, plActuals, plMappings, currentMonthIdx }) {
  const [view, setView] = useState('pnl');
  const [cfPlatExpanded, setCfPlatExpanded] = useState(false);
  const [cfRoyExpanded,  setCfRoyExpanded]  = useState(false);
  const fc = functionalCurrency || 'USD';
  const cf = cashflowData || {};

  return (
    <div className="panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <span style={{ fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--t3)' }}>Statement</span>
        <div className="tgl-pair">
          {canSeePL && <button className={`tbtn yes ${view === 'pnl' ? 'on' : ''}`} onClick={() => setView('pnl')}>P&amp;L</button>}
          {canSeeCF && <button className={`tbtn yes ${view === 'cf' ? 'on' : ''}`} onClick={() => setView('cf')}>Cash Flow</button>}
        </div>
      </div>

      {view === 'pnl' && (
        <PnLContent games={games} PS={PS} CS={CS} DT={DT} startDate={startDate}
          functionalCurrency={functionalCurrency} convert={convert}
          peopleDevTotal={peopleDevTotal} peopleOhTotal={peopleOhTotal}
          ohCategories={ohCategories} ohValues={ohValues}
          getCategoryMonthlyTotal={getCategoryMonthlyTotal}
          getOhAllMonthlyTotal={getOhAllMonthlyTotal}
          plActuals={plActuals} plMappings={plMappings} currentMonthIdx={currentMonthIdx} />
      )}

      {view === 'cf' && (
        <div className="card" style={{ margin: '0 -4px' }}>
          <div className="card-head">
            <span className="card-title">Cash Flow Statement</span>
            <span className="card-meta">36-Month Forecast{fc !== 'USD' ? ` · ${fc}` : ''}</span>
          </div>
          <div className="card-body">
            <div className="tw">
              <table className="dt">
                <thead><FullHeader label="Line Item" startDate={startDate} /></thead>
                <tbody>
                  <SectionHeader label="Cash Inflows" />
                  {/* Collapsible Platform Receipts */}
                  <tr className="rc" style={{ cursor: 'pointer' }} onClick={() => setCfPlatExpanded(e => !e)}>
                    <td><span style={{ fontSize: 9, color: 'var(--blue)', marginRight: 6 }}>{cfPlatExpanded ? '▾' : '▸'}</span>Platform Receipts</td>
                    {[0,1,2].map(y => <td key={`y${y}`} style={{background:'var(--s2)',fontWeight:600,borderLeft:'2px solid var(--e2)'}}>{fu(annualSum(cf.receipts||[],y*12))}</td>)}
                    {(cf.receipts || Array(NM).fill(0)).map((v,i) => <td key={i}>{v ? fu(v) : '—'}</td>)}
                  </tr>
                  {cfPlatExpanded && (cf.platformBreakdown || []).map(({ key, label, paid }) => (
                    <tr key={key} className="rc" style={{ background: 'var(--s0)' }}>
                      <td style={{ paddingLeft: 20, fontSize: 10, color: 'var(--t3)' }}>↳ {label}</td>
                      {[0,1,2].map(y => <td key={`y${y}`} style={{background:'var(--s1)',fontSize:10,color:'var(--t3)',borderLeft:'2px solid var(--e2)'}}>{fu(annualSum(paid,y*12))}</td>)}
                      {paid.map((v, i) => <td key={i} style={{ fontSize: 10, color: 'var(--t3)' }}>{v ? fu(v) : '—'}</td>)}
                    </tr>
                  ))}
                  <CalcRow label="Total Cash Inflows" values={cf.receipts || Array(NM).fill(0)} cls="rt" convert={null} fc={fc} startDate={startDate} />
                  <SpacerRow />
                  <SectionHeader label="Cash Outflows" />
                  <CalcRow label="Development Advances" values={(cf.devOut || Array(NM).fill(0)).map(v => -v)} cls="rc" convert={null} fc={fc} startDate={startDate} />
                  <CalcRow label="Publishing Costs"     values={(cf.pubOut || Array(NM).fill(0)).map(v => -v)} cls="rc" convert={null} fc={fc} startDate={startDate} />
                  {/* Collapsible Royalties */}
                  <tr className="rc" style={{ cursor: 'pointer' }} onClick={() => setCfRoyExpanded(e => !e)}>
                    <td><span style={{ fontSize: 9, color: 'var(--blue)', marginRight: 6 }}>{cfRoyExpanded ? '▾' : '▸'}</span>Royalties Payable</td>
                    {[0,1,2].map(y => <td key={`y${y}`} style={{background:'var(--s2)',fontWeight:600,borderLeft:'2px solid var(--e2)'}}>{annualSum(cf.royOut||[],y*12) ? `(${fu(annualSum(cf.royOut||[],y*12))})` : '—'}</td>)}
                    {(cf.royOut || Array(NM).fill(0)).map((v,i) => <td key={i}>{v ? `(${fu(v)})` : '—'}</td>)}
                  </tr>
                  {cfRoyExpanded && (cf.royaltyBreakdown || []).map(({ game, paid }) => (
                    <tr key={game} className="rc" style={{ background: 'var(--s0)' }}>
                      <td style={{ paddingLeft: 20, fontSize: 10, color: 'var(--t3)' }}>↳ {game}</td>
                      {[0,1,2].map(y => <td key={`y${y}`} style={{background:'var(--s1)',fontSize:10,color:'var(--t3)',borderLeft:'2px solid var(--e2)'}}>{annualSum(paid,y*12) ? `(${fu(annualSum(paid,y*12))})` : '—'}</td>)}
                      {paid.map((v, i) => <td key={i} style={{ fontSize: 10, color: 'var(--t3)' }}>{v ? `(${fu(v)})` : '—'}</td>)}
                    </tr>
                  ))}
                  <CalcRow label="Payroll"              values={(cf.payrollOut || Array(NM).fill(0)).map(v => -v)} cls="rc" convert={null} fc={fc} startDate={startDate} />
                  {(ohCategories || []).filter(c => !c.belowEbitda).map(cat => {
                    const catTotals = getCategoryMonthlyTotal ? getCategoryMonthlyTotal(cat.id) : Array(NM).fill(0);
                    if (!catTotals.some(v => v > 0)) return null;
                    return <CalcRow key={cat.id} label={cat.name} values={catTotals.map(v => -v)} cls="rc" convert={null} fc={fc} startDate={startDate} />;
                  })}
                  <CalcRow label="Total Cash Outflows"  values={(Array(NM).fill(0).map((_, i) => -((cf.devOut?.[i]||0) + (cf.pubOut?.[i]||0) + (cf.royOut?.[i]||0) + (cf.payrollOut?.[i]||0) + (cf.ohOut?.[i]||0))))} cls="rt" convert={null} fc={fc} startDate={startDate} />
                  <SpacerRow />
                  {/* Net = receipts - all outflows */}
                  <CalcRow label="Net Cash Movement"
                    values={Array(NM).fill(0).map((_, i) =>
                      (cf.receipts?.[i]||0) - (cf.devOut?.[i]||0) - (cf.pubOut?.[i]||0) - (cf.royOut?.[i]||0) - (cf.payrollOut?.[i]||0) - (cf.ohOut?.[i]||0)
                    )}
                    cls="rg" convert={null} fc={fc} startDate={startDate} />
                  <SpacerRow />
                  <SectionHeader label="Cash Position" />
                  {/* Opening Cash — annual col shows first month of year */}
                  {(() => {
                    const vals = cf.open || Array(NM).fill(0);
                    const annuals = [0,1,2].map(y => vals[y*12] || 0);
                    return (
                      <tr className="rc">
                        <td>Opening Cash</td>
                        {annuals.map((v,y) => <td key={`y${y}`} style={{background:'var(--s2)',fontWeight:600,borderLeft:'2px solid var(--e2)'}}>{fu(v)}</td>)}
                        {vals.map((v,i) => <td key={i}>{fu(v)}</td>)}
                      </tr>
                    );
                  })()}
                  {/* Closing Cash — annual col shows last month of year */}
                  {(() => {
                    const vals = cf.close || Array(NM).fill(0);
                    const annuals = [0,1,2].map(y => vals[Math.min(y*12+11,NM-1)] || 0);
                    return (
                      <tr className="rg">
                        <td>Closing Cash</td>
                        {annuals.map((v,y) => <td key={`y${y}`} style={{background:'var(--s2)',fontWeight:700,borderLeft:'2px solid var(--e2)',color:v>=0?'var(--green)':'var(--red)'}}>{fu(v)}</td>)}
                        {vals.map((v,i) => <td key={i} style={{color:v>=0?'var(--green)':'var(--red)',fontWeight:600}}>{fu(v)}</td>)}
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
